/**
 * Simple PHP serialized array parser for JavaScript
 * Handles basic PHP serialized arrays in the format: a:2:{i:0;s:9:"Company A";i:1;s:9:"Company B";}
 */

/**
 * Parse a PHP serialized array into a JavaScript array
 * @param {string} serialized - The PHP serialized string
 * @returns {Array|null} - JavaScript array or null if invalid input
 */
export const parsePhpArray = (serialized) => {
  if (!serialized || typeof serialized !== 'string') {
    return null;
  }

  // Check if it's a PHP serialized array
  if (!serialized.startsWith('a:')) {
    return null;
  }

  try {
    const result = [];
    // Extract the count from a:COUNT:{...}
    const countMatch = serialized.match(/^a:(\d+):/);
    if (!countMatch) return null;
    
    // Get the content inside the curly braces
    const contentMatch = serialized.match(/^a:\d+:{(.*)}/s);
    if (!contentMatch) return null;
    
    const content = contentMatch[1];
    
    // Pattern to match: i:INDEX;s:LENGTH:"VALUE";
    const pattern = /i:\d+;s:(\d+):"([^"]*)";/g;
    
    let match;
    while ((match = pattern.exec(content)) !== null) {
      const value = match[2];
      result.push(value);
    }
    
    return result;
  } catch (error) {
    console.error('Error parsing PHP serialized array:', error);
    return null;
  }
};

/**
 * Extract first value from PHP serialized array
 * @param {string} serialized - The PHP serialized string
 * @returns {string} - First value or empty string if parsing fails
 */
export const getFirstValueFromPhpArray = (serialized) => {
  const parsed = parsePhpArray(serialized);
  return parsed && parsed.length > 0 ? parsed[0] : '';
};

/**
 * Get formatted value from PHP serialized array or plain string
 * Adds special processing for supplier BP codes
 * @param {string} value - The value which might be serialized PHP
 * @param {string} type - The type of data being processed ('supplier', 'user', etc.)
 * @returns {string} - Formatted string ready for display
 */
export const getFormattedValue = (value, type = '') => {
  if (!value || typeof value !== 'string') {
    return '';
  }
  
  // Check if it's likely a PHP serialized array
  if (value.startsWith('a:')) {
    const parsedArray = parsePhpArray(value);
    
    if (!parsedArray || parsedArray.length === 0) {
      return '';
    }
    
    // Special handling for supplier BP codes
    if (type === 'supplier') {
      // If the value is just a numeric BP code, return something more readable
      const firstValue = parsedArray[0];
      
      // Check if it's a numeric ID (BP code)
      if (/^\d+$/.test(firstValue)) {
        // If the second value exists and isn't numeric, it might be the company name
        if (parsedArray.length > 1 && !/^\d+$/.test(parsedArray[1]) && parsedArray[1].length > 2) {
          return parsedArray[1]; // Return the company name if available
        }
        return 'Supplier #' + firstValue;
      }
      
      // Check if it's a supplier code format (e.g., "10000-ABC")
      const supplierCodeMatch = firstValue.match(/^(\d+)[-_](.+)$/);
      if (supplierCodeMatch) {
        // Return the second part if it looks like a name
        if (supplierCodeMatch[2].length > 2 && /[a-zA-Z]/.test(supplierCodeMatch[2])) {
          return supplierCodeMatch[2];
        }
        return 'Supplier #' + supplierCodeMatch[1];
      }
      
      return firstValue;
    }
    
    // For user data, join names if multiple exist
    if (type === 'user' && parsedArray.length > 1) {
      // Filter out non-name looking values (e.g. numeric IDs)
      const possibleNames = parsedArray.filter(item => /[a-zA-Z]/.test(item));
      if (possibleNames.length > 0) {
        return possibleNames[0];
      }
    }
    
    return parsedArray[0] || '';
  }
  
  // Return the original value if it's not a serialized array
  return value;
};
